import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, defer, EMPTY, from, Observable, Subject } from 'rxjs';
import { catchError, exhaustMap, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AppsService, ProjectsService, SitesService, TokenService } from '.';
import { environment } from '../../environments/environment';
import { AuthUser, User } from '../models';
import { AUTH_CONSOLE_MESSAGES, AUTH_ERROR_CODES, AUTH_MESSAGES, AUTH_PROVIDER_TYPES, AUTH_ROUTES, AUTH_STORAGE_KEYS } from '../shared/constants';
import { EmailHelper } from '../shared/helpers';
import { AuthDataService } from './data-services';
import { IdentityProviderService, IdentityProviderType } from './identity-provider.service';
import { UsersLogicService } from './logic-services';
import { StorageUtilityService } from './utility-services';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    private _destroy$ = new Subject<void>();
    private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _authenticated = new BehaviorSubject<boolean>(false);
    private _showTOS = new BehaviorSubject<boolean>(false);

    authenticated$ = this._authenticated.asObservable();
    showTOS$ = this._showTOS.asObservable();
    loading$ = this._loading.asObservable();
    returnUrl: string | undefined = undefined;

    constructor(
        private _authData: AuthDataService,
        private _dialog: MatDialog,
        private _router: Router,
        private _identityProvider: IdentityProviderService,
        private _usersLogic: UsersLogicService,
        private _appsService: AppsService,
        private _projectsService: ProjectsService,
        private _sitesService: SitesService,
        private _snackBar: MatSnackBar,
        private _storageUtility: StorageUtilityService,
        private _tokenService: TokenService,
    ) {
        this.initializeAuthenticationListener();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    authenticateUser(email: string): Observable<AuthUser> {
        const isInternalUser = EmailHelper.isInternalUser(email);
        const providerType = isInternalUser ? AUTH_PROVIDER_TYPES.ENTRA_ID : AUTH_PROVIDER_TYPES.ENTRA_EXTERNAL_ID;

        return defer(() => from(this._identityProvider.setIdentityProvider(providerType))).pipe(
            switchMap(() => this.authenticateWithProvider(providerType, email))
        );
    }

    private configureUser(response: AuthUser): void {
        const token = response.token;
        this._tokenService.setToken(token);
        this._usersLogic.setAuthUser(response);

        this._usersLogic.loadCurrentUser(response.id)
            .pipe(
                takeUntil(this._destroy$)
            )
            .subscribe({
                next: (user: User) => {
                    if (!response.acceptedTOS) {
                        this._showTOS.next(true);
                    }
                    else {
                        this._showTOS.next(false);
                        this.setAuthenticated(true);
                        this.redirectAfterLogin(user);
                        this._loading.next(false);
                    }
                },
                error: () => {
                    this._loading.next(false);
                    this._snackBar.open(AUTH_MESSAGES.USER_LOAD_ERROR, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 5000 });
                }
            });
    }

    private setAuthenticated(val: boolean): void {
        this._authenticated.next(val);
    }

    setShowTOS(val: boolean): void {
        this._showTOS.next(val);
    }

    setRoutedApp(app: string): void {
        this._storageUtility.setItem(AUTH_STORAGE_KEYS.ROUTED_APP, app);
    }

    setReturnUrl(url: string): void {
        this.returnUrl = url;
    }

    setIsLoading(value: boolean): void {
        this._loading.next(value);
    }

    logout(): void {
        this._dialog.closeAll();
        this._storageUtility.clear();
        this._identityProvider.clearActiveAccounts();
        this._identityProvider.clearIdentityProvider();
        this._usersLogic.disconnectUser();
        this._appsService.disconnectApp();
        this._projectsService.disconnectProject();
        this._projectsService.disconnectProjects();
        this._sitesService.disconnectSite();
        this._sitesService.disconnectSites();
        this.setReturnUrl('');
        this.setAuthenticated(false);
        this._router.navigate([AUTH_ROUTES.HOME]);
    }

    redirectAfterLogin(user: User): void {
        if (this.returnUrl) {
            const returnUrl = this.returnUrl;
            this.setReturnUrl('');
            this._router.navigate([returnUrl]);
            return;
        }

        if (!user.isPlatformAdmin && !user.active) {
            this._router.navigate([AUTH_ROUTES.HOME]);
            return;
        }

        this._router.navigate(AUTH_ROUTES.PLATFORM_USER_APPS);

        return;
    }

    private initializeAuthenticationListener(): void {
        this._identityProvider.redirectResult$.pipe(
            filter((result: AuthenticationResult | null) => !!result),
            exhaustMap(() => this.handleAuthenticationSuccess()),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    private handleAuthenticationSuccess(): Observable<AuthUser> {
        const providerType = this._identityProvider.currentIdentityProviderValue;

        if (providerType === AUTH_PROVIDER_TYPES.UNKNOWN || this._authenticated.value) {
            return EMPTY;
        }

        return defer(() => from(this._identityProvider.getActiveInstance())).pipe(
            switchMap((instance: IPublicClientApplication | null) => this.acquireTokenForInstance(instance, providerType)),
            tap(() => this._snackBar.open(AUTH_MESSAGES.AUTH_SUCCESS, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 5000 })),
            switchMap((result: AuthenticationResult) => this.loginUserAPI(result.accessToken)),
            catchError((error: any) => {
                console.error(AUTH_CONSOLE_MESSAGES.AUTHENTICATION_FAILED, error);
                this._loading.next(false);

                if (error instanceof InteractionRequiredAuthError) {
                    this.logout();
                }
                else if (error?.errorCode === AUTH_ERROR_CODES.AUTHORITY_MISMATCH || error?.message?.includes(AUTH_ERROR_CODES.AUTHORITY_MISMATCH)) {
                    this._identityProvider.clearIdentityProvider();
                }

                this._snackBar.open(AUTH_MESSAGES.AUTH_FAILED, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 5000 });
                return EMPTY;
            })
        );
    }

    private acquireTokenForInstance(instance: IPublicClientApplication | null, providerType: IdentityProviderType): Observable<AuthenticationResult> {
        if (!instance) {
            return EMPTY;
        }

        const account = instance.getActiveAccount();
        if (!account) {
            return EMPTY;
        }

        this._loading.next(true);

        const scopes = providerType === AUTH_PROVIDER_TYPES.ENTRA_ID
            ? [environment.entraId.scopes]
            : [environment.entraExternalId.scopes];

        return defer(() => from(instance.acquireTokenSilent({ scopes, account })));
    }

    private authenticateWithProvider(providerType: 'EntraExternalId' | 'EntraId', loginHint?: string): Observable<AuthUser> {
        this._loading.next(true);
        const isInternal = providerType === AUTH_PROVIDER_TYPES.ENTRA_ID;

        return defer(() => from(
            isInternal
                ? this._identityProvider.getOrCreateInternalInstance()
                : this._identityProvider.getOrCreateExternalInstance()
        )).pipe(
            switchMap((msalInstance: IPublicClientApplication) =>
                this.handleInstanceAuthentication(msalInstance, loginHint, isInternal)
            )
        );
    }

    private handleInstanceAuthentication(msalInstance: IPublicClientApplication, loginHint: string | undefined, isInternal: boolean): Observable<AuthUser> {
        const config = this.getAuthConfig(isInternal);

        if (loginHint) {
            const account = msalInstance
                .getAllAccounts()
                .find(acc => acc.username.toLowerCase() === loginHint.toLowerCase()) ?? null;

            if (account) {
                msalInstance.setActiveAccount(account);
                return this.performSilentAuthentication(msalInstance, config, account, loginHint);
            }

            this.performRedirectAuthentication(msalInstance, config, loginHint);
            return EMPTY;
        }

        const account = this.resolveAccountForLogin(msalInstance);
        if (this.shouldRedirectForLogin(account, loginHint)) {
            this.performRedirectAuthentication(msalInstance, config, loginHint);
            return EMPTY;
        }

        return this.performSilentAuthentication(msalInstance, config, account, "");
    }

    private getAuthConfig(isInternal: boolean) {
        return isInternal
            ? { scopes: [environment.entraId.scopes], redirectUri: environment.entraId.redirectUri }
            : { scopes: [environment.entraExternalId.scopes], redirectUri: environment.entraExternalId.redirectUri };
    }

    private resolveAccountForLogin(msalInstance: IPublicClientApplication, loginHint?: string): AccountInfo | null {
        const activeAccount = msalInstance.getActiveAccount();

        if (!loginHint) {
            return activeAccount;
        }

        if (activeAccount && activeAccount.username.toLowerCase() === loginHint.toLowerCase()) {
            return activeAccount;
        }

        const matchingAccount = msalInstance
            .getAllAccounts()
            .find(account => account.username.toLowerCase() === loginHint.toLowerCase()) ?? null;

        if (matchingAccount) {
            msalInstance.setActiveAccount(matchingAccount);
        }

        return matchingAccount;
    }

    private shouldRedirectForLogin(account: AccountInfo | null, loginHint?: string): boolean {
        if (!account) {
            return true;
        }

        if (!loginHint) {
            return false;
        }

        return (account.username.toLowerCase() !== loginHint.toLowerCase());
    }

    private performRedirectAuthentication(msalInstance: IPublicClientApplication, config: { scopes: string[]; redirectUri: string; }, loginHint?: string): void {
        const redirectScope = this.buildRedirectRequest(config.scopes, config.redirectUri, loginHint);
        this.acquireTokenRedirect(msalInstance, redirectScope);
    }

    private performSilentAuthentication(msalInstance: IPublicClientApplication, config: { scopes: string[]; redirectUri: string; }, account: AccountInfo | null, submittedEmail: string): Observable<AuthUser> {
        const scope: RedirectRequest = this.buildRedirectRequest(config.scopes, config.redirectUri, submittedEmail);

        if (account) {
            scope.account = account;
        }

        return defer(() => from(msalInstance.acquireTokenSilent(scope))).pipe(
            filter((result: AuthenticationResult) => !!result && !!result.account && (!submittedEmail || result?.account.username.toLowerCase() === submittedEmail.toLowerCase())),
            tap((result: AuthenticationResult) => {
                msalInstance.setActiveAccount(result.account);
            }),
            tap(() => this._snackBar.open(AUTH_MESSAGES.AUTH_SUCCESS, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 5000 })),
            switchMap((result: AuthenticationResult) => this.loginUserAPI(result.accessToken)),
            catchError((error: any) => {
                console.error(AUTH_CONSOLE_MESSAGES.SILENT_TOKEN_ERROR, error);
                this.setReturnUrl(this._router.url);
                this.acquireTokenRedirect(msalInstance, scope);
                return EMPTY;
            })
        );
    }

    private acquireTokenRedirect(msalInstance: IPublicClientApplication, scope: RedirectRequest) {
        msalInstance.acquireTokenRedirect(scope).catch((redirectError: any) => {
            console.error(AUTH_CONSOLE_MESSAGES.REDIRECT_TOKEN_ERROR, redirectError);
            this._loading.next(false);
            this._snackBar.open(AUTH_MESSAGES.AUTH_FAILED, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 5000 });
        });
    }

    private loginUserAPI(adToken: string): Observable<AuthUser> {
        return this._authData.loginUserAPI(adToken).pipe(
            tap(response => {
                if (response.token === "-1") {
                    this._loading.next(false);
                    this._snackBar.open(AUTH_MESSAGES.ACCOUNT_DISABLED, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 5000 });
                }
                else {
                    this.configureUser(response);
                }
            }),
            catchError((error: any) => {
                this._loading.next(false);
                if (error.status === 401) {
                    this._router.navigate([AUTH_ROUTES.HOME, AUTH_ROUTES.ACCOUNT_MAPPING_FAILED]);
                    this._snackBar.open(AUTH_MESSAGES.NO_ACCOUNT, AUTH_MESSAGES.CLOSE_BUTTON);
                }
                else {
                    this._snackBar.open(AUTH_MESSAGES.LOGIN_FAILED, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 5000 });
                }
                return EMPTY;
            })
        );
    }

    renewToken(tokenEmail: string): Observable<string | null> {
        return from(this.acquireFreshTokenSilent(tokenEmail));
    }

    private async acquireFreshTokenSilent(tokenEmail: string): Promise<string | null> {
        const instance = await this._identityProvider.getActiveInstance();

        if (!instance || !tokenEmail) {
            return null;
        }

        const account = instance.getActiveAccount();

        if (!account) {
            return null;
        }

        if (account.username.toLowerCase() !== tokenEmail.toLowerCase()) {
            return null;
        }

        const providerType = this._identityProvider.currentIdentityProviderValue;
        const scopes = providerType === AUTH_PROVIDER_TYPES.ENTRA_ID
            ? [environment.entraId.scopes]
            : [environment.entraExternalId.scopes];

        try {
            const result = await instance.acquireTokenSilent({
                scopes,
                account,
                forceRefresh: true
            });

            this._tokenService.setToken(result.accessToken);

            return result.accessToken;
        }
        catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                console.warn(AUTH_CONSOLE_MESSAGES.INTERACTION_REQUIRED);
                return this.handleInteractionRequiredForTokenRenewal(instance, scopes, providerType);
            }

            console.error(AUTH_CONSOLE_MESSAGES.AUTHENTICATION_FAILED, error);

            return null;
        }
    }

    private async handleInteractionRequiredForTokenRenewal(
        instance: IPublicClientApplication,
        scopes: string[],
        providerType: IdentityProviderType
    ): Promise<null> {
        this.setReturnUrl(this._router.url);

        const redirectUri = providerType === AUTH_PROVIDER_TYPES.ENTRA_ID
            ? environment.entraId.redirectUri
            : environment.entraExternalId.redirectUri;

        try {
            await instance.acquireTokenRedirect(this.buildRedirectRequest(scopes, redirectUri));
        }
        catch (redirectError) {
            console.error(AUTH_CONSOLE_MESSAGES.AUTHENTICATION_FAILED, redirectError);
            this.logout();
        }

        return null;
    }

    private buildRedirectRequest(scopes: string[], redirectUri: string, loginHint?: string): RedirectRequest {
        return {
            scopes,
            redirectUri,
            redirectStartPage: this.getRedirectStartPageWithAuthenticatingFlag(),
            ...(loginHint ? { loginHint } : {})
        };
    }

    private getRedirectStartPageWithAuthenticatingFlag(): string {
        const currentPath = this._router.url || '/';
        const redirectStart = new URL(currentPath, window.location.origin);
        redirectStart.searchParams.set('authenticating', 'true');
        return redirectStart.toString();
    }
}
